<template>
    <div>
        <skeleton-loader v-if="loading" type="rows" :count="4" />
        <template v-else>
            <div class="injury-list">
                <div class="injury-item" v-for="injury in currentInjuries" :key="injury.id">
                    <span class="injury-name">{{ getInjuryName(injury) }}</span>
                    <div v-if="!readonly" class="injury-actions">
                        <button type="button" class="injury-btn edit" @click="loadInjury(injury)">
                            <v-icon size="14" color="accent">$edit</v-icon>
                        </button>
                        <button type="button" class="injury-btn del" @click="showDeleteInjury(injury)">
                            <v-icon size="14" color="error">$delete</v-icon>
                        </button>
                    </div>
                </div>

                <div v-if="injuries.length === 0" class="injury-item injury-item--empty">
                    {{ $t('common.without_injuries') }}
                </div>
            </div>

            <div v-if="showFooter" class="injury-footer">
                <button v-if="addButton && !headerAdd" type="button" class="injury-add" @click="showInjuryForm">
                    <v-icon size="20" color="accent">$plus</v-icon>
                </button>
                <kimsa-pagination v-if="pageCount > 1" @page="page = $event - 1" :pageCount="pageCount"
                    :total-visible="isMobile ? 5 : 7" :value="page + 1" />
            </div>
        </template>

        <kimsa-confirm ref="delete" :title="$t('common.delete_injury')" :content="$t('common.confirm_delete_injury')"
            :loading="loadingDelete" :persistent="false" @confirm="onConfirmDelete" @cancel="currentInjury = null"
            :close-on-confirm="false" :alert-mode="true" />

        <injury-form-dialog :studentId="studentId" :coachId="coachId" ref="injury" @success="loadInjuries" />
    </div>
</template>

<script>
import InjuryFormDialog from "@/components/student/InjuryFormDialog";
import { db } from "@/utils/Firebase";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import KimsaConfirm from '../common/KimsaConfirm.vue';
import KimsaPagination from '../common/KimsaPagination.vue';

export default {
    name: "InjuriesCard",
    components: { SkeletonLoader, InjuryFormDialog, KimsaConfirm, KimsaPagination },
    props: {
        studentId: {
            type: String,
            default: null,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        addButton: {
            type: Boolean,
            default: false,
        },
        headerAdd: {
            type: Boolean,
            default: false,
        },
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            loadingDelete: false,
            currentInjury: null,
            injuries: [],
            page: 0,
            limit: 5,
        }
    },
    async mounted() {
        const vm = this
        await vm.loadInjuries()
    },
    computed: {
        currentInjuries(vm = this) {
            let currentPage = (vm.page * vm.limit)
            return vm.injuries.slice(currentPage, currentPage + vm.limit)
        },
        pageCount(vm = this) {
            return Math.ceil(vm.injuries.length / vm.limit)
        },
        showFooter(vm = this) {
            return (vm.addButton && !vm.headerAdd) || vm.pageCount > 1
        }
    },
    methods: {
        showInjuryForm() {
            const vm = this
            vm.$refs.injury.show()
        },
        async loadInjuries() {
            const vm = this
            await vm.withLoading(async () => {
                vm.injuries = []

                let ref = await db.collection('injuries')
                    .where('studentId', '==', vm.studentId)
                    .orderBy('date', 'desc')
                if (vm.addButton) {
                    ref = await ref.get()
                } else {
                    ref = await ref.limit(3).get()
                }
                vm.injuries = ref.docs.map(injury => ({
                    ...injury.data(),
                    id: injury.id,
                }))
            })
        },
        loadInjury(injury) {
            const vm = this
            vm.$refs.injury.loadInjury(injury)
        },
        showDeleteInjury(injury) {
            const vm = this
            vm.currentInjury = injury
            vm.$refs.delete.show(injury)
        },
        async onConfirmDelete() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('injuries').doc(vm.currentInjury.id).delete()
                vm.currentInjury = null
                vm.$refs.delete?.hide()
                vm.loadInjuries()
            }, 'loadingDelete')
        },
        getInjuryName(injury) {
            const vm = this
            let date = vm.$moment(injury.date).format('MMMM YYYY')
            return `${vm.injuryTypeLabel(injury.type)} ${vm.injuryZoneLabel(injury.zone)} — ${date}`
        }
    },
}
</script>

<style scoped>
.injury-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.injury-item {
    display: flex;
    align-items: center;
    gap: 10px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 11px 14px;
}

.injury-item--empty {
    color: var(--v-txtMuted-base);
    font-size: 13px;
}

.injury-name {
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    color: var(--v-accent-base);
    text-transform: capitalize;
}

.injury-actions {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.injury-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.15s;
}

.injury-btn:hover {
    opacity: 0.8;
}

.injury-btn.edit {
    background: rgba(51, 153, 153, 0.15);
}

.injury-btn.del {
    background: rgba(232, 92, 92, 0.15);
}

.injury-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
}

.injury-add {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid var(--v-outline2-base);
    background: var(--v-surface-base);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s;
}

.injury-add:hover {
    background: var(--v-surface2-base);
}
</style>
